/**
 * NoticeBanner — per-user in-app notices shown at the top of the Stage until
 * dismissed (2026-09-03). First use: the drop-fee refund card.
 * Mobile mirror: mobile/src/components/NoticeBanner.js
 */
import { useEffect, useState } from 'react';
import { BadgeDollarSign, Bell, Gift, X } from 'lucide-react';
import { noticesAPI } from '../lib/api';
import { Button } from './ui/button';

const KIND_ICON = { refund: BadgeDollarSign, budget_boost: Gift };

export default function NoticeBanner({ onAction }) {
  const [notices, setNotices] = useState([]);

  useEffect(() => {
    let alive = true;
    noticesAPI.list()
      .then((r) => { if (alive) setNotices(r.data?.notices || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const dismiss = async (id) => {
    setNotices((prev) => prev.filter((n) => n.id !== id));
    try { await noticesAPI.dismiss(id); } catch { /* already gone locally */ }
  };

  if (!notices.length) return null;
  return (
    <div className="space-y-3 mb-4" data-testid="notice-banner">
      {notices.map((n) => {
        const Icon = KIND_ICON[n.kind] || Bell;
        return (
          <div
            key={n.id}
            className="relative rounded-2xl border border-[hsl(var(--accent)/0.35)] bg-[hsl(var(--accent)/0.08)] p-4 pr-10 animate-in fade-in slide-in-from-top-2 duration-300"
            data-testid={`notice-${n.id}`}
            data-notice-kind={n.kind}
          >
            <button
              type="button"
              onClick={() => dismiss(n.id)}
              className="absolute right-2 top-2 rounded-full p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
              aria-label="Dismiss"
              data-testid={`notice-dismiss-${n.id}`}
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-full bg-[hsl(var(--accent)/0.15)] p-2 text-[hsl(var(--accent))]">
                <Icon className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold leading-tight" data-testid={`notice-title-${n.id}`}>{n.title}</p>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed" data-testid={`notice-body-${n.id}`}>{n.body}</p>
                {n.cta?.label && onAction && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-3 h-7 text-xs"
                    onClick={() => onAction(n.cta.view)}
                    data-testid={`notice-cta-${n.id}`}
                  >
                    {n.cta.label}
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
